import React, { useState } from 'react';
import { getAllMethods, getRoleMethods } from './toolRegistryData';

const ROLE_LABELS: Record<string, string> = {
  supervisor: 'Supervisor',
  medical_analyst: 'Medical Analyst',
  scheduler: 'Scheduler',
  gmail_agent: 'Gmail Agent',
  log_agent: 'Log Agent',
  general_assistant: 'General Assistant',
  function: 'Function',
};

const CATEGORY_ICONS: Record<string, string> = {
  files: '📁',
  shell: '💻',
  medical: '🏥',
  gmail: '📧',
  scheduling: '📅',
  logging: '📝',
  flows: '🔄',
  memory: '🧠',
};

export const ToolRegistryView: React.FC = () => {
  const allMethods = getAllMethods();
  const [enabledTools, setEnabledTools] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem('toolRegistry_enabledTools');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) return new Set(parsed);
      }
    } catch {}
    return new Set(allMethods);
  });
  const [filter, setFilter] = useState('');

  const roleMethods = getRoleMethods();

  const toggleTool = (method: string) => {
    setEnabledTools((prev) => {
      const next = new Set(prev);
      if (next.has(method)) next.delete(method);
      else next.add(method);
      try { localStorage.setItem('toolRegistry_enabledTools', JSON.stringify([...next])); } catch {}
      return next;
    });
  };

  const categories = Array.from(new Set(allMethods.map((m) => m.split('.')[0])));

  const filteredMethods = filter
    ? allMethods.filter((m) => m.includes(filter.toLowerCase()))
    : allMethods;

  return (
    <div className="flex flex-col h-full dark:bg-claude-darkBg bg-claude-bg">
      <div className="px-4 py-3 border-b dark:border-claude-darkBorder border-claude-border">
        <h2 className="text-lg font-semibold dark:text-claude-darkText text-claude-text">Tool Registry</h2>
        <p className="text-xs mt-1 dark:text-claude-darkTextSecondary text-claude-textSecondary">
          View all available JSON-RPC methods and which roles can access them.
        </p>
        <input
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="input-base mt-2"
          placeholder="Filter methods..."
        />
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
        {categories.map((cat) => {
          const catMethods = filteredMethods.filter((m) => m.startsWith(`${cat}.`));
          if (catMethods.length === 0) return null;

          return (
            <div key={cat}>
              <h3 className="text-xs font-semibold uppercase tracking-wide mb-2 dark:text-claude-darkTextSecondary text-claude-textSecondary">
                {CATEGORY_ICONS[cat] || '⚡'} {cat}
              </h3>
              <div className="space-y-1">
                {catMethods.map((method) => {
                  const rolesWithAccess = Object.entries(roleMethods)
                    .filter(([, methods]) => methods.includes(method))
                    .map(([role]) => ROLE_LABELS[role] || role);
                  const isEnabled = enabledTools.has(method);

                  return (
                    <div
                      key={method}
                      className="flex items-center justify-between rounded-lg border dark:border-claude-darkBorder border-claude-border px-3 py-2 dark:bg-claude-darkSurface bg-claude-surface"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-mono dark:text-claude-darkText text-claude-text truncate">
                          {method}
                        </div>
                        {rolesWithAccess.length > 0 && (
                          <div className="text-[10px] mt-0.5 dark:text-claude-darkTextSecondary text-claude-textSecondary truncate">
                            Roles: {rolesWithAccess.join(', ')}
                          </div>
                        )}
                      </div>
                      <label className="flex items-center cursor-pointer ml-2">
                        <input
                          type="checkbox"
                          checked={isEnabled}
                          onChange={() => toggleTool(method)}
                          className="!w-3.5 !h-3.5"
                        />
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div className="px-4 py-2 border-t dark:border-claude-darkBorder border-claude-border">
        <p className="text-xs dark:text-claude-darkTextSecondary text-claude-textSecondary">
          {enabledTools.size}/{allMethods.length} tools enabled
        </p>
      </div>
    </div>
  );
};